import { useMemo } from 'react';
import { gql } from '@apollo/client';
import { EmbeddedContentDefinition, TemplatesAndContents } from 'modules/graphql';
import { DisplayPriorityCampaign } from 'modules/placements/types';
import { useGraphql } from 'utils/hooks/useGraphql';

export type EmbeddedCampaignWithContent = DisplayPriorityCampaign & {
  content: EmbeddedContentDefinition;
};

export const fetchEmbeddedTemplatesAndContents = gql`
  query FetchEmbeddedTemplatesAndContents($ids: [Long!]!) {
    templatesAndContents(ids: $ids) {
      __typename
      ... on TemplateAndContent {
        templateId
        primaryContent {
          ... on EmbeddedContentDefinition {
            embeddedContentId
            title
            body
            elements {
              mediaUrl
              defaultAction {
                type
                data
              }
              buttons {
                id
                title
                action {
                  type
                  data
                }
              }
              text {
                id
                label
                text
              }
            }
            listId
            placementId
            payload
          }
        }
      }
      ... on CorruptedTemplateData {
        contentId
      }
    }
  }
`;

const formatResponse = (data: any, campaigns: DisplayPriorityCampaign[]) => {
  const responseData = data.templatesAndContents || {};
  const templatesAndContents = responseData as TemplatesAndContents;

  return templatesAndContents.map((template) => {
    return {
      ...campaigns.find((campaign) => template.templateId === campaign.templateId),
      content: template.primaryContent as EmbeddedContentDefinition,
    } as EmbeddedCampaignWithContent;
  });
};

export const useFetchEmbeddedTemplatesByCampaigns = (campaigns: DisplayPriorityCampaign[]) => {
  const { data, isLoading } = useGraphql(fetchEmbeddedTemplatesAndContents, {
    variables: {
      ids: campaigns.map((campaign) => campaign.templateId),
    },
    fetchPolicy: 'cache-first',
  });

  const results = useMemo(
    () => (data?.templatesAndContents ? formatResponse(data, campaigns) : []),
    [data, campaigns],
  );

  return {
    results,
    isLoading,
  };
};
